import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { loadSeller, loadUser } from "../../redux/actions/user";
import { getCurrentUserFollowers } from '../../redux/actions/followers';
import { fetchMyConnections } from '../../redux/actions/connections';
import { server } from '../../server';
import { getStoredAuthToken } from '../../utils/authSession';
import { getStoredCurrentProfile, isShopProfile } from '../../utils/shopProfile';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const FALLBACK_AVATAR = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22 viewBox=%220 0 80 80%22%3E%3Crect width=%2280%22 height=%2280%22 rx=%2240%22 fill=%22%2323292f%22/%3E%3Ccircle cx=%2240%22 cy=%2231%22 r=%2214%22 fill=%22%236ee000%22/%3E%3Cpath d=%22M17 70c4-16 16-25 23-25s19 9 23 25%22 fill=%22%236ee000%22 opacity=%22.75%22/%3E%3C/svg%3E';

const mergeMembersByProfile = (...memberLists) => {
  const membersByKey = new Map();

  memberLists
    .flat()
    .filter((member) => member?._id)
    .forEach((member) => {
      const type = member.type || member.profileType || 'User';
      const key = `${type}:${member._id}`;

      if (!membersByKey.has(key)) {
        membersByKey.set(key, {
          ...member,
          type,
          name: member.name || 'Member',
          avatar: member.avatar || '',
        });
      }
    });

  return Array.from(membersByKey.values());
};

export default function RoomAddMemberForm({ roomId, currentMembers, currentInvites = [], onMemberAdded, onClose }) {
  const [open, setOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('Followers');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const dispatch = useDispatch();
  const currentProfile = useSelector((state) => state.user.currentProfile) || getStoredCurrentProfile() || 'User';
  const { user } = useSelector((state) => state.user);
  const { seller, activeSeller } = useSelector((state) => state.seller);
  const { currentUserFollowers, currentUserFollowing, isLoading: followersLoading, error: followersError } = useSelector(state => state.followers);
  const {
    myFollowers,
    myFollowing,
    isLoadingMyFollowers,
    isLoadingMyFollowing,
    error: connectionsError,
  } = useSelector((state) => state.connections);

  const profileIsShop = isShopProfile(currentProfile) && currentProfile !== 'defaultProfile';
  const selectedShop = seller || activeSeller;
  const profileId = profileIsShop ? selectedShop?._id : user?._id;
  const profileType = profileIsShop ? 'Shop' : 'User';

  useEffect(() => {
    if (!profileIsShop && !user?._id) {
      dispatch(loadUser());
    }

    if (profileIsShop && !selectedShop?._id) {
      dispatch(loadSeller(currentProfile));
    }
  }, [currentProfile, dispatch, profileIsShop, selectedShop?._id, user?._id]);

  useEffect(() => {
    if (profileId && profileType) {
      dispatch(fetchMyConnections(profileId, profileType, 'followers')).catch(() => {});
      dispatch(fetchMyConnections(profileId, profileType, 'following')).catch(() => {});
      dispatch(getCurrentUserFollowers(profileId, profileType));
    }
  }, [dispatch, profileId, profileType]);

  const tabs = [
    { name: 'Followers', current: activeTab === 'Followers' },
    { name: 'Following', current: activeTab === 'Following' },
  ];

  const handleMemberSelect = (id, type) => {
    setSelectedMembers((prevSelectedMembers) =>
      prevSelectedMembers.some(member => member.id === id && member.type === type)
        ? prevSelectedMembers.filter(member => !(member.id === id && member.type === type))
        : [...prevSelectedMembers, { id, type }]
    );
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const filteredMembers = (() => {
    const members = activeTab === 'Followers'
      ? mergeMembersByProfile(myFollowers, currentUserFollowers)
      : mergeMembersByProfile(myFollowing, currentUserFollowing);
    const currentRoomMembers = Array.isArray(currentMembers) ? currentMembers : [];
    const pendingRoomInvites = Array.isArray(currentInvites) ? currentInvites : [];
    const filtered = members.filter(
      member => 
        (member.name || '').toLowerCase().includes(searchTerm) &&
        !currentRoomMembers.some(currentMember => currentMember._id === member._id && currentMember.type === member.type) &&
        !pendingRoomInvites.some(invite => invite.profileId === member._id && invite.profileType === member.type)
    );
    return filtered;
  })();

  const activeConnectionsLoading = activeTab === 'Followers' ? isLoadingMyFollowers : isLoadingMyFollowing;
  const isConnectionsLoading = Boolean(activeConnectionsLoading || followersLoading);
  const activeError = filteredMembers.length === 0 ? connectionsError || followersError : '';

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    const token = getStoredAuthToken();
    const headers = { 'current-profile': currentProfile || 'User' };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await axios.post(
        `${server}/room/rooms/${roomId}/add-members`,
        { memberIds: selectedMembers },
        {
          withCredentials: true,
          headers,
        }
      );
      setSuccess(response.data?.message || 'Invitations sent successfully!');
      setSelectedMembers([]);
      onMemberAdded();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invitations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="add-member-form-container" onClose={() => { setOpen(false); onClose(); }}>
        <div className="add-member-form-overlay" />
        <div className="add-member-form-wrapper">
          <div className="add-member-form-inner">
            <div className="add-member-form-content">
              <Transition.Child
                as={Fragment}
                enter="add-member-form-enter"
                enterFrom="add-member-form-enter-from"
                enterTo="add-member-form-enter-to"
                leave="add-member-form-leave"
                leaveFrom="add-member-form-leave-from"
                leaveTo="add-member-form-leave-to"
              >
                <Dialog.Panel className="add-member-form-panel">
                  <div className="add-member-form-body">
                    <div className="add-member-form-header">
                      <Dialog.Title className="add-member-form-title">Invite Members to Room</Dialog.Title>
                      <div className="add-member-form-close-button-wrapper">
                        <button
                          type="button"
                          className="add-member-form-close-button"
                          onClick={() => { setOpen(false); onClose(); }}
                        >
                          <XMarkIcon className="add-member-form-icon" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    <div className="add-member-form-tabs">
                      <nav className="add-member-form-tabs-nav" aria-label="Tabs">
                        {tabs.map((tab) => (
                          <a
                            key={tab.name}
                            href="#"
                            className={classNames(
                              tab.current ? 'add-member-form-tab-active' : 'add-member-form-tab-inactive',
                              'add-member-form-tab'
                            )}
                            aria-current={tab.current ? 'page' : undefined}
                            onClick={(event) => {
                              event.preventDefault();
                              setActiveTab(tab.name);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                setActiveTab(tab.name);
                              }
                            }}
                          >
                            {tab.name}
                          </a>
                        ))}
                      </nav>
                    </div>
                  </div>

                  <div className="add-member-form-user-selection">
                    <input
                      type="text"
                      placeholder={`Search ${activeTab.toLowerCase()}`}
                      className="add-member-form-search"
                      onChange={handleSearchChange}
                    />
                    <div className="add-member-form-list">
                      {isConnectionsLoading && <p>Loading...</p>}
                      {!isConnectionsLoading && activeError && <p>{activeError}</p>}
                      {!isConnectionsLoading && !activeError && filteredMembers.length > 0 ? (
                        filteredMembers.map((member) => (
                          <div key={`${member.type}-${member._id}`} className="add-member-form-user-item">
                            <label>
                              <input
                                type="checkbox"
                                checked={selectedMembers.some(selected => selected.id === member._id && selected.type === member.type)}
                                onChange={() => handleMemberSelect(member._id, member.type)}
                              />
                              <img
                                src={member.avatar || FALLBACK_AVATAR}
                                alt={member.name || 'Member'}
                                className="add-member-form-user-avatar"
                              />
                              <span>{member.name || 'Member'}</span>
                            </label>
                          </div>
                        ))
                      ) : (
                        !isConnectionsLoading && !activeError && (
                          <p>{profileId ? 'No members found.' : 'Loading your profile connections...'}</p>
                        )
                      )}
                    </div>
                  </div>

                  <div className="add-member-form-actions">
                    <button
                      type="button"
                      className="add-member-form-submit"
                      onClick={handleSubmit}
                      disabled={loading || selectedMembers.length === 0}
                    >
                      {loading ? 'Sending invitations...' : 'Send Invite Requests'}
                    </button>
                    {error && <p className="error-message">{error}</p>}
                    {success && <p className="success-message">{success}</p>}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}




